import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Pencil, Plus, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { useToast } from '@/components/ui/use-toast';
import MembershipOrganizationFormDialog from '../components/MembershipOrganizationFormDialog';
import { membershipOrganizationsService } from '../../services/membershipOrganizations';
import type { MembershipOrganization } from '../../types/membership';
import { useCurrentGym } from '../hooks/useCurrentGym';
import { useGymAdmin } from '../context/gym-admin-context';

/** "31/8", or nothing at all for a body with no fixed season. */
function expiryLabel(organization: MembershipOrganization): string | null {
  if (organization.expiry_day === null || organization.expiry_month === null) return null;
  return `${organization.expiry_day}/${organization.expiry_month}`;
}

/**
 * The sports bodies a membership card can be issued by.
 *
 * Two kinds of row, and the difference is who owns them: the ones DivePeaks
 * maintains are shown so staff can see what is already available to them, and
 * the gym adds its own underneath. Only the latter carry controls.
 */
export default function MembershipOrganizationsPage() {
  const { t } = useTranslation();
  const { toast } = useToast();
  const navigate = useNavigate();
  const { placePath } = useGymAdmin();
  const { gym, loading: gymLoading, slug } = useCurrentGym();

  const [organizations, setOrganizations] = useState<MembershipOrganization[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<MembershipOrganization | null>(null);

  const load = useCallback(
    async (gymSlug: string) => {
      setLoading(true);
      try {
        setOrganizations(await membershipOrganizationsService.list(gymSlug));
      } catch (error) {
        console.error('Failed to load membership organizations:', error);
        toast({ title: t('gymAdmin.membershipOrganizations.loadError'), variant: 'destructive' });
      } finally {
        setLoading(false);
      }
    },
    [t, toast]
  );

  useEffect(() => {
    if (slug) load(slug);
  }, [slug, load]);

  const openNew = () => {
    setEditing(null);
    setDialogOpen(true);
  };

  const openEdit = (organization: MembershipOrganization) => {
    setEditing(organization);
    setDialogOpen(true);
  };

  const handleSaved = () => {
    setDialogOpen(false);
    if (slug) load(slug);
  };

  const handleDelete = async (organization: MembershipOrganization) => {
    if (!slug) return;
    const confirmed = window.confirm(
      t('gymAdmin.membershipOrganizations.confirmDelete', { name: organization.name })
    );
    if (!confirmed) return;

    try {
      await membershipOrganizationsService.remove(slug, organization.id);
      setOrganizations((prev) => prev.filter((row) => row.id !== organization.id));
      toast({ title: t('gymAdmin.membershipOrganizations.deleted') });
    } catch (error) {
      toast({
        title: t('gymAdmin.membershipOrganizations.deleteError'),
        description: error instanceof Error ? error.message : t('errors.unexpectedError'),
        variant: 'destructive',
      });
    }
  };

  if (gymLoading) return <Skeleton className="h-96 w-full max-w-3xl" />;
  if (!gym || !slug) return null;

  const platformWide = organizations.filter((row) => row.platform_wide);
  const own = organizations.filter((row) => !row.platform_wide);

  const renderRow = (organization: MembershipOrganization) => {
    const expiry = expiryLabel(organization);

    return (
      <Card key={organization.id}>
        <CardContent className="flex flex-wrap items-center gap-4 p-4">
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <h3 className="truncate font-medium">{organization.name}</h3>
              {organization.global_cards && (
                <Badge
                  variant="secondary"
                  title={t('gymAdmin.membershipOrganizations.globalCardsHint')}
                >
                  {t('gymAdmin.membershipOrganizations.globalCards')}
                </Badge>
              )}
            </div>
            <p className="mt-1 text-sm text-muted-foreground">
              {expiry
                ? t('gymAdmin.membershipOrganizations.expiresEvery', { date: expiry })
                : t('gymAdmin.membershipOrganizations.noFixedExpiry')}
            </p>
          </div>

          {organization.editable && (
            <div className="flex shrink-0 items-center gap-1">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => openEdit(organization)}
                aria-label={t('common.edit')}
              >
                <Pencil className="size-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="text-destructive"
                onClick={() => handleDelete(organization)}
                aria-label={t('common.delete')}
              >
                <Trash2 className="size-4" />
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="max-w-3xl space-y-6">
      <Button variant="ghost" size="sm" onClick={() => navigate(placePath('/members'))}>
        <ArrowLeft className="mr-2 size-4" />
        {t('gymAdmin.members.backToList')}
      </Button>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-muted-foreground">
          {t('gymAdmin.membershipOrganizations.subtitle')}
        </p>
        <Button onClick={openNew}>
          <Plus className="mr-2 size-4" />
          {t('gymAdmin.membershipOrganizations.new')}
        </Button>
      </div>

      {loading ? (
        <div className="space-y-3">
          <Skeleton className="h-20 w-full" />
          <Skeleton className="h-20 w-full" />
        </div>
      ) : (
        <>
          <section className="space-y-3">
            <h2 className="text-sm font-medium text-muted-foreground">
              {t('gymAdmin.membershipOrganizations.platformWideTitle')}
            </h2>
            {platformWide.length === 0 ? (
              <Card>
                <CardContent className="py-8 text-center text-muted-foreground">
                  {t('gymAdmin.membershipOrganizations.noPlatformWide')}
                </CardContent>
              </Card>
            ) : (
              platformWide.map(renderRow)
            )}
          </section>

          <section className="space-y-3">
            <h2 className="text-sm font-medium text-muted-foreground">
              {t('gymAdmin.membershipOrganizations.ownTitle')}
            </h2>
            {own.length === 0 ? (
              <Card>
                <CardContent className="py-8 text-center text-muted-foreground">
                  {t('gymAdmin.membershipOrganizations.empty')}
                </CardContent>
              </Card>
            ) : (
              own.map(renderRow)
            )}
          </section>
        </>
      )}

      <MembershipOrganizationFormDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        gymSlug={slug}
        organization={editing}
        onSaved={handleSaved}
      />
    </div>
  );
}
